import * as yup from "yup";


const applyRequired = (schema, required, field) => {
    return required
        ? schema.required(`${field} is required`)
        : schema.nullable().notRequired();
};
/**
 * Generic Text
 * Allows letters, numbers, spaces, hyphens, underscores, dots, brackets, etc.
 * Good for Tenant Name, Company Name, Project Name
 */
export const textAreaRule = (
    field = "Field",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .trim()
        .test(
            "no-multiple-spaces",
            `${field} cannot contain multiple consecutive spaces`,
            value => !value || !/\s{2,}/.test(value)
        )
        .matches(
            /^[A-Za-z0-9\s\-_.,()[\]{}'"/\\|:;?`~!@#$%^&*]+$/,
            `${field} can contain letters, numbers, and common special characters`
        )
        .max(500, `${field} must not exceed 500 characters`);

    return applyRequired(schema, required, field);
};


/**
 * Generic Text
 * Allows letters, numbers, spaces, hyphens, underscores, dots, brackets, etc.
 * Good for Tenant Name, Company Name, Project Name
 */
export const textRule = (
    field = "Field",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .trim()
        .test(
            "no-multiple-spaces",
            `${field} cannot contain multiple consecutive spaces`,
            value => !value || !/\s{2,}/.test(value)
        )
        .matches(
            /^[A-Za-z0-9\s\-_.,()[\]{}'"/\\|:;?`~!@#$%^&*]+$/,
            `${field} can contain letters, numbers, and common special characters`
        )
        .max(50, `${field} must not exceed 50 characters`);

    return applyRequired(schema, required, field);
};



/**
 * First Name / Last Name
 * Letters and spaces only
 */
export const nameRule = (
    field = "Name",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .trim()
        .matches(
            /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
            `${field} can contain only letters and a single space between words`
        )
        .min(2, `${field} must be at least 2 characters`)
        .max(50, `${field} must not exceed 50 characters`);

    return applyRequired(schema, required, field);
};



/**
 * Email
 */
export const emailRule = (
    field = "Email",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .trim()
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            "Invalid email address")
        .email("Invalid email address")
        .max(50, `${field} must not exceed 50 characters`);

    return applyRequired(schema, required, field);
};



/**
 * Phone Number
 */
export const phoneRule = (
    field = "Phone Number",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .test(
            "phone",
            `${field} must contain 10 digits`,
            value => {
                if (!value) return true;
                return /^[0-9]{10}$/.test(value);
            }
        );

    return applyRequired(schema, required, field);
};

/**
 * Password
 */
export const passwordRule = (
    field = "Password",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .min(13, "Password must be at least 13 characters")
        .max(20, `${field} must not exceed 20 characters`)
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
            /[!@#$%^&*(),.?":{}|<>_\-\\[\]`~+=/;'%]/,
            "Password must contain at least one special character"
        );

    return applyRequired(schema, required, field);
};
/**
 * Domain
 * example.com
 */
export const domainRule = (
    field = "Domain",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .trim()
        .matches(
            /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
            "Invalid domain"
        )
        .max(50, `${field} must not exceed 50 characters`);

    return applyRequired(schema, required, field);
};

/**
 * Credit
 * Positive integer
 */
export const creditRule = (
    field = "Credits",
    { required = false } = {}
) => {
    let schema = yup
        .number()
        .typeError(`${field} must be a number`)
        .integer(`${field} must be an integer`)
        .min(0, `${field} cannot be negative`)
        .max(1000000, `${field} must upto 1000000`);


    return applyRequired(schema, required, field);
};


/**
 * Price
 * Allows decimals
 */
export const priceRule = (
    field = "Price",
    { required = false } = {}
) => {
    let schema = yup
        .number()
        .typeError(`${field} must be a valid number`)
        .min(0, `${field} cannot be negative`)
        .max(100000, `${field} must upto 100000`);

    return applyRequired(schema, required, field);
};
/**
 * Allowed Recruiters
 */
export const recruitersRule = (
    field = "Allowed Recruiters",
    { required = false } = {}
) => {
    let schema = yup
        .number()
        .typeError(`${field} must be a number`)
        .integer(`${field} must be an integer`)
        .min(1, `${field} must be at least 1`)
        .max(500, `${field} must upto 500`);

    return applyRequired(schema, required, field);
};


/**
 * Plan End in Days
 */
export const planEndDaysRule = (
    field = "Plan End in Days",
    { required = false } = {}
) => {
    let schema = yup
        .number()
        .typeError(`${field} must be a number`)
        .integer(`${field} must be an integer`)
        .min(1, `${field} must be at least 1`)
        .max(2000, `${field} must upto 2000`);


    return applyRequired(schema, required, field);
};


/**
 * Date
 */
export const dateRule = (
    field = "Date",
    { required = false } = {}
) => {
    let schema = yup
        .date()
        .typeError(`Invalid ${field}`);

    return applyRequired(schema, required, field);
};



/**
 * URL
 */
export const urlRule = (
    field = "URL",
    { required = false } = {}
) => {
    let schema = yup
        .string()
        .trim()
        .url(`Invalid ${field}`);

    return applyRequired(schema, required, field);
};
/**
 * Required Dropdown
 */
export const selectRule = (
    field = "Selection",
    { required = false } = {}
) => {
    let schema = yup.string();

    return applyRequired(schema, required, field);
};



/**
 * Positive Integer
 */
export const positiveIntegerRule = (
    field = "Value",
    { required = false } = {}
) => {
    let schema = yup
        .number()
        .typeError(`${field} must be a number`)
        .integer(`${field} must be an integer`)
        .positive(`${field} must be greater than 0`);

    return applyRequired(schema, required, field);
};